import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "./AppShell";
import InfoCard from "./InfoCard";
import Error404 from "../404/404";
import { getApp } from "../../../Constants/Apps";
import { APPTOTO_WHATSAPP } from "../../../Constants/AppsContentApptoto";
import { useDocumentTitle } from "../../../Hooks/useDocumentTitle";
import { useTranslation } from "../../../Hooks/useTranslation";

// Fila de un dato para la transferencia con botón para copiarlo.
const DatoPago = ({ label, value, copy, copied }) => {
  const [listo, setListo] = useState(false);
  const copiar = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setListo(true);
      setTimeout(() => setListo(false), 1600);
    });
  };
  return (
    <div className="plan-bank-row">
      <span className="plan-bank-label">{label}</span>
      <span className="plan-bank-value">{value}</span>
      <button type="button" className="plan-copy" onClick={copiar}>
        {listo ? copied : copy}
      </button>
    </div>
  );
};

// Contratar un plan de pago (por ahora solo Apptoto Pro). Precios y transferencia viven aquí, fuera de la app.
const AppContrata = () => {
  const { appId } = useParams();
  const { t } = useTranslation();
  const app = getApp(appId);
  const [anual, setAnual] = useState(false);

  return (
    <AppShell>
      {({ content }) => {
        const c = content.contrata;
        if (!c) return <Error404 />;
        return <Contenido c={c} app={app} t={t} anual={anual} setAnual={setAnual} />;
      }}
    </AppShell>
  );
};

const Contenido = ({ c, app, t, anual, setAnual }) => {
  useDocumentTitle(`${c.pro.name} · ${app.name}`);
  const plan = anual ? c.pro.yearly : c.pro.monthly;
  const whatsapp = `https://wa.me/${APPTOTO_WHATSAPP}?text=${encodeURIComponent(c.whatsappMessage)}`;
  const hayDatos = Boolean(c.pago?.cuenta);

  return (
    <>
      <section className="section app-section">
        <div className="container">
          <div data-reveal>
            <span className="eyebrow">{c.eyebrow}</span>
            <h2>{c.title}</h2>
            <p className="plan-intro">{c.intro}</p>
          </div>

          <div className="plan-switch" role="group" aria-label={c.pro.name} data-reveal>
            <button type="button" className={!anual ? "is-on" : ""} onClick={() => setAnual(false)}>
              {c.pro.monthly.label}
            </button>
            <button type="button" className={anual ? "is-on" : ""} onClick={() => setAnual(true)}>
              {c.pro.yearly.label}
              <span className="plan-save">{c.pro.yearly.badge}</span>
            </button>
          </div>

          <div className="plan-grid">
            <div className="plan-card" data-reveal style={{ "--d": 0 }}>
              <h3>{c.free.name}</h3>
              <p className="plan-note">{c.free.note}</p>
              <p className="plan-price">
                {c.free.price}
                <span>{anual ? c.perYear : c.perMonth}</span>
              </p>
              <ul className="plan-list">
                {c.free.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </div>

            <div className="plan-card is-pro" data-reveal style={{ "--d": 1 }}>
              <span className="plan-popular">{c.pro.popular}</span>
              <h3>{c.pro.name}</h3>
              <p className="plan-note">{c.pro.note}</p>
              <p className="plan-price" aria-live="polite">
                {plan.price}
                <span>{plan.period}</span>
              </p>
              <ul className="plan-list">
                {c.pro.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <a className="btn plan-whatsapp" href={whatsapp} target="_blank" rel="noopener noreferrer">
                {c.whatsapp}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="section app-section">
        <div className="container">
          <span className="eyebrow" data-reveal>{c.stepsTitle}</span>
          <div className="row app-features">
            {c.steps.map((paso, index) => (
              <InfoCard key={paso.title} item={paso} col="col-12 col-md-6 col-lg-3" style={{ "--d": index }} />
            ))}
          </div>

          {hayDatos && (
            <div className="plan-bank" data-reveal>
              <h3>{c.bankTitle}</h3>
              {c.pago.banco && <DatoPago label={c.bank} value={c.pago.banco} copy={c.copy} copied={c.copied} />}
              <DatoPago label={c.account} value={c.pago.cuenta} copy={c.copy} copied={c.copied} />
              {c.pago.titular && <DatoPago label={c.holder} value={c.pago.titular} copy={c.copy} copied={c.copied} />}
            </div>
          )}

          <div className="plan-cta" data-reveal>
            <a className="btn plan-whatsapp" href={whatsapp} target="_blank" rel="noopener noreferrer">
              {c.whatsapp}
            </a>
            <p>{c.whatsappNote}</p>
          </div>
        </div>
      </section>

      <section className="section app-section">
        <div className="container">
          <span className="eyebrow" data-reveal>{c.faqTitle}</span>
          <div className="row app-cards">
            {c.faq.map((item, index) => (
              <InfoCard key={item.title} item={item} style={{ "--d": index % 3 }} />
            ))}
            <div className="col-12 col-md-6">
              <Link to={`/apps/${app.id}/terminos`} className="info-card info-card-privacy" data-reveal>
                <div className="info-card-media" aria-hidden="true"><span>📄</span></div>
                <div className="info-card-body">
                  <h3>{t.apps.termsTitle}</h3>
                  <p>{t.apps.termsCta}</p>
                  <span className="info-card-cta">{t.apps.readTerms} →</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AppContrata;
